import { inject, Injectable, signal } from '@angular/core';
import { ProblemResolveDto, RecommendationAction } from '@shared/models';
import { TroubleshootingStore } from '@shared/state';
import { Observable, throwError } from 'rxjs';
import { QueryParameterService } from '@shared/services';

export const TROUBLESHOOTING_SESSION_STORAGE_KEY = 'troubleshootingFilters';
@Injectable({
  providedIn: 'root',
})
export class TroubleshootingService {
  protected readonly queryParametersService = inject(QueryParameterService);
  private readonly troubleshootingStore = inject(TroubleshootingStore);
  readonly machine = signal<number | undefined>(undefined);
  readonly faultCode = signal<number | undefined>(undefined);

  /**
   * Returns a cold observable. The request is sent when the caller subscribes.
   */
  setRecommendationStatus({ recommendationId, recommendationStatus }: RecommendationAction): Observable<void> {
    const faultCode = this.faultCode();
    const machine = this.machine();

    if (!machine || !faultCode) {
      return throwError(() => new Error('Missing Machine.'));
    }

    return this.troubleshootingStore.setRecommendationStatus({ machine, faultCode, recommendationId, recommendationStatus });
  }

  /**
   * Returns a cold observable. The request is sent when the caller subscribes.
   */
  solveProblem(props?: ProblemResolveDto): Observable<void> {
    const faultCode = this.faultCode();
    const machine = this.machine();

    if (!machine || !faultCode) {
      return throwError(() => new Error('Missing Machine.'));
    }

    return this.troubleshootingStore.solveProblem({
      machine,
      faultCode,
      ...props,
    });
  }

  setMachine(machine: number): void {
    if (this.machine() === machine) {
      return;
    }

    this.machine.set(machine);
    this.troubleshootingStore.setSelectedMachine(machine);

    void this.queryParametersService.setQueryParams({ key: TROUBLESHOOTING_SESSION_STORAGE_KEY, queryParams: { machine } });
  }

  setFaultCode(faultCode: number): void {
    if (this.faultCode() === faultCode) {
      return;
    }

    this.faultCode.set(faultCode);
    this.troubleshootingStore.setFaultCode(faultCode);

    void this.queryParametersService.setQueryParams({ key: TROUBLESHOOTING_SESSION_STORAGE_KEY, queryParams: { faultCode } });

    const machine = this.machine();
    if (faultCode && machine) {
      this.troubleshootingStore.fetchRecommendations({ machine, faultCode });
    }
  }

  setTroubleshootingFilters({ machine, faultCode }: { machine: number; faultCode: number }): void {
    this.setMachine(machine);
    this.setFaultCode(faultCode);
  }
}
